// src/pages/ClinicalDataCollector/components/output/sections/COPDSection.jsx

import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import SmokeFreeIcon from '@mui/icons-material/SmokeFree';
import HistoryIcon from '@mui/icons-material/History';

const COPDSection = ({ data }) => {
  if (!data) return null;

  const {
    code,
    description,
    goldClassification,
    spirometry = [],
    immunizations = [],
    maintenanceTherapy = [],
    exacerbationHistory = {},
    nonPharma = []
  } = data;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Section: COPD"
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<LocalHospitalIcon fontSize="large" color="primary" />}
      />
      <Divider />
      <CardContent>
        {/* ICD-10 Code */}
        <Typography
          variant="subtitle1"
          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
        >
          <LocalHospitalIcon sx={{ mr: 1 }} />
          Code: {code} — {description}
        </Typography>

        {/* GOLD Classification */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <ShowChartIcon sx={{ mr: 1 }} /> GOLD Classification
        </Typography>
        <Typography variant="body2" paragraph>
          {goldClassification}
        </Typography>

        {/* Spirometry */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <ShowChartIcon sx={{ mr: 1 }} /> Spirometry
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Measure</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {spirometry.map((row, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{row.measure}</TableCell>
                <TableCell>{row.value}</TableCell>
                <TableCell>{row.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Immunizations */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <VaccinesIcon sx={{ mr: 1 }} /> Preventive Immunizations
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {immunizations.map((imm, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <VaccinesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={imm} />
            </ListItem>
          ))}
        </List>

        {/* Maintenance Therapy */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LocalPharmacyIcon sx={{ mr: 1 }} /> Maintenance Therapy
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {maintenanceTherapy.map((tx, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <LocalPharmacyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={tx} />
            </ListItem>
          ))}
        </List>

        {/* Exacerbation History */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <HistoryIcon sx={{ mr: 1 }} /> Exacerbation History
        </Typography>
        <Typography variant="body2" paragraph>
          Number in the last 12 months:{' '}
          <strong>{exacerbationHistory.count}</strong>
          <br />
          Last exacerbation: {exacerbationHistory.lastDate}
        </Typography>

        {/* Non-Pharmacologic Interventions */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <SmokeFreeIcon sx={{ mr: 1 }} /> Non-Pharmacologic Interventions
        </Typography>
        <List dense>
          {nonPharma.map((item, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <SmokeFreeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default COPDSection;
